import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatSnackBar } from '@angular/material';
import { NameFormat } from 'src/API';
import { Assessment, AssessmentService } from 'src/app/core/api/assessment.service';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { noSort } from 'src/app/core/api/model-utils';
import { PatientService } from 'src/app/core/api/patient.service';
import { S3AttachmentService } from 'src/app/shared/attachments/s3-attachment.service';
import { defaultLogoUri } from 'src/app/shared/dry-eye-pdf-export/default-logo-uri.model';
import { MedicalHistoryLayoutService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/medical-history-layout/medical-history-layout.service';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { SymptomMethod } from 'src/app/shared/symptoms/symptom.model';
import { ThermalScaleService } from 'src/app/shared/symptoms/thermal-scale/thermal-scale.service';
import { SelectAssessmentsModalComponent } from '../select-assessments-modal/select-assessments-modal.component';
import { ClinicSetupService } from './../../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LayoutsService } from './../../logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { LEFT_EYE_KEY_PREFIX, RIGHT_EYE_KEY_PREFIX, SYMPTOM_METHOD_KEY_SUFFIX } from './../../shared/form.model';

export type EncountersToCompare = Assessment[];

type StatusRelativeToFirst = 'added' | 'removed' | 'changed' | 'same' | 'improving' | 'worsening';

interface ComparedFields {
  [key: string]: {
    [key: string]: {
      value: any;
      statusRelativeToFirst?:
        | { left: StatusRelativeToFirst; right: StatusRelativeToFirst }
        | StatusRelativeToFirst;
    };
  };
}

@Component({
  selector: 'csi-encounters-compare-modal',
  templateUrl: './encounters-compare-modal.component.html',
  styleUrls: ['./encounters-compare-modal.component.scss']
})
export class EncountersCompareModalComponent implements OnDestroy {
  public readonly MAX_COMPARE = 4;
  public readonly noSort = noSort;
  public highlightDates: { [createdAt: string]: 'left' | 'right' } = {};

  get remainingSlots(): number {
    return Math.max(this.MAX_COMPARE - this.data.length, 0);
  }

  public normalizedValues: {
    medicalHistoryForm: ComparedFields;
    dryEyeForm: ComparedFields;
  } = { medicalHistoryForm: {}, dryEyeForm: {} };

  public combinedMedicalHistorySet: Set<any>;
  public combinedSignsSet: Set<any>;
  public encounterDateOrder: string[];
  public maxValueMap: { [key: string]: number } = {};
  public isNumericMap: { [fieldKey: string]: { [createdAt: string]: boolean } } = {};
  public normalizedWidthMap: { [fieldKey: string]: { [createdAt: string]: number } } = {};

  public checkboxKeyValueMap = {
    ...this.schemaService.checkboxConditionMap,
    ...this.schemaService.medicalConditionMap
  };

  public readonly fieldsToSkip = ['schemaVersion', 'EYE_MARKUP'];
  public encountersNotSelectedForComparison: Assessment[];
  public readonly NameFormat = NameFormat;
  public readonly defaultLogo = defaultLogoUri;
  public clinicLogoDataUri: { url: any } | null = null;

  private moveHighlightTimeout: any;
  private assessmentMethodCache: { [id: string]: string } = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EncountersToCompare,
    public layoutsService: LayoutsService,
    public schemaService: SchemaService,
    public patientService: PatientService,
    public medicalHistoryLayoutService: MedicalHistoryLayoutService,
    public assessmentService: AssessmentService,
    public dialog: MatDialog,
    private thermalScaleService: ThermalScaleService,
    public clinicSetupService: ClinicSetupService,
    private clinicService: ClinicService,
    private s3AttachmentService: S3AttachmentService
  ) {
    this.normalize();
    this.loadClinicLogo();
  }

  private loadClinicLogo() {
    const clinicId = this.clinicSetupService.clinicId;
    if (!clinicId) return;
    this.clinicService.getClinicLogo(clinicId).subscribe(logo => {
      if (!logo) return;
      this.s3AttachmentService.s3ObjectToUri(logo).subscribe(urlWithContentType => {
        this.clinicLogoDataUri = urlWithContentType;
      });
    });
  }

  public printReport() {
    const clinicName = (this.clinicSetupService.clinic && this.clinicSetupService.clinic.name) || '';
    const ph = this.printHeaderData;
    const patient = ph.patient;
    const rawName = patient
      ? ((patient.firstName || '') + ' ' + (patient.lastName || '')).trim()
      : '';
    const patientName = rawName.replace(/\b\w/g, c => c.toUpperCase());
    const assessmentMethod = this.getAssessmentMethodFriendlyName(this.data && this.data[0]);
    const baselineDate =
      this.encounterDateOrder && this.encounterDateOrder.length
        ? new Date(this.encounterDateOrder[0]).toLocaleDateString()
        : '';
    const originalTitle = document.title;
    document.title = [clinicName, patientName, 'Assessment Compare', assessmentMethod, baselineDate]
      .filter(s => s && s !== 'N/A')
      .join(' - ');
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  }

  get printHeaderData(): { patient: any; doctor: any; clinic: any } {
    if (!this.data || !this.data[0] || !this.data[0].body) {
      return { patient: null, doctor: null, clinic: null };
    }
    try {
      const parsed = JSON.parse(this.data[0].body);
      return {
        patient: parsed.patientInformationForm || null,
        doctor: parsed.doctorInformationForm || null,
        clinic: parsed.clinicInformationForm || null
      };
    } catch (e) {
      return { patient: null, doctor: null, clinic: null };
    }
  }

  ngOnDestroy() {
    if (this.moveHighlightTimeout) {
      clearTimeout(this.moveHighlightTimeout);
    }
  }

  public static open(
    dialog: MatDialog,
    encountersToCompare: EncountersToCompare,
    snackBar?: MatSnackBar
  ) {
    const MAX = 4;
    const selections = encountersToCompare || [];

    if (selections.length > MAX) {
      const ref = dialog.open(SelectAssessmentsModalComponent, {
        width: '520px',
        data: {
          currentCompared: selections,
          unaddedAssessments: [],
          maxSelectable: 0
        }
      });

      ref.afterClosed().subscribe((updated: any) => {
        if (updated && updated.length && updated.length <= MAX) {
          dialog.open(EncountersCompareModalComponent, {
            data: updated,
            width: '100vw',
            maxWidth: '95vw'
          });
        }
      });
      return;
    }

    dialog.open(EncountersCompareModalComponent, {
      data: selections,
      width: '100vw',
      maxWidth: '95vw'
    });
  }

  normalize() {
    this.normalizedValues = { medicalHistoryForm: {}, dryEyeForm: {} };
    this.maxValueMap = {};
    this.isNumericMap = {};
    this.normalizedWidthMap = {};
    this.encounterDateOrder = this.data.map(encounter => encounter.createdAt);

    const [combinedMedicalHistorySet, combinedSignsSet] = this.getCombinedFieldsForAllEncounters();
    this.combinedMedicalHistorySet = combinedMedicalHistorySet;
    this.combinedSignsSet = combinedSignsSet;

    const encounterIdSet = new Set(this.data.map(encounter => encounter.id));
    this.encountersNotSelectedForComparison = this.patientService.assessments.filter(
      assessment => !encounterIdSet.has(assessment.id)
    );

    let isFirst = true;
    const firstComparedEncounterValueMap: { [key: string]: any } = {};

    this.data.forEach(encounter => {
      if (encounter.body) {
        this.normalizeValueForMedicalHistoryForm(isFirst, encounter, firstComparedEncounterValueMap);
        this.normalizeValueForDryEyeForm(isFirst, encounter, firstComparedEncounterValueMap);
      }
      isFirst = false;
    });
    this.buildMedicalHistoryBarMaps();
  }

  private buildMedicalHistoryBarMaps(): void {
    const mhForm = this.normalizedValues['medicalHistoryForm'];
    if (!mhForm) return;

    Object.keys(mhForm).forEach(fieldKey => {
      const byDate = mhForm[fieldKey];
      if (!byDate) return;

      this.isNumericMap[fieldKey] = {};
      this.normalizedWidthMap[fieldKey] = {};

      Object.keys(byDate).forEach(createdAt => {
        const value = byDate[createdAt] ? byDate[createdAt].value : null;
        const isNum = this.isNumeric(value);
        this.isNumericMap[fieldKey][createdAt] = isNum;
        this.normalizedWidthMap[fieldKey][createdAt] = isNum
          ? this.normalizeValue(value, this.maxValueMap[fieldKey])
          : 0;
      });
    });
  }

  normalizeValueForMedicalHistoryForm(
    isFirst: boolean,
    encounter: Assessment,
    firstComparedEncounterValueMap: { [key: string]: any }
  ) {
    const parsedBody = JSON.parse(encounter.body);
    const parsedMedicalHistoryForm = parsedBody['medicalHistoryForm'] || {};
    const applicableFields = this.medicalHistoryLayoutService.allApplicableMedicalHistoryForAssessment(
      { controls: parsedMedicalHistoryForm } as any
    );

    applicableFields.forEach(formKey => {
      const currentFieldValue = this.schemaService.getFriendlyValueName(
        parsedMedicalHistoryForm[formKey],
        formKey
      );

      const num = parseFloat(currentFieldValue);
      if (!isNaN(num)) {
        const currentMax = this.maxValueMap[formKey];
        this.maxValueMap[formKey] = currentMax === undefined ? num : Math.max(currentMax, num);
      }

      const comparedField = {
        value: currentFieldValue,
        statusRelativeToFirst: 'same' as StatusRelativeToFirst
      };

      if (isFirst) {
        firstComparedEncounterValueMap[formKey] = currentFieldValue;
      } else {
        comparedField['statusRelativeToFirst'] = this.getStatusBasedOnFirstEncounter(
          firstComparedEncounterValueMap[formKey],
          currentFieldValue
        );
      }

      this.normalizedValues['medicalHistoryForm'][formKey] = {
        [encounter.createdAt]: comparedField,
        ...this.normalizedValues['medicalHistoryForm'][formKey]
      };
    });
  }

  normalizeValueForDryEyeForm(
    isFirst: boolean,
    encounter: Assessment,
    firstComparedEncounterValueMap: { [key: string]: any }
  ) {
    const parsedBody = JSON.parse(encounter.body);
    const dryEyeForm = parsedBody['dryEyeForm'];

    this.combinedSignsSet.forEach(formKey => {
      const rightKey = RIGHT_EYE_KEY_PREFIX + formKey;
      const leftKey = LEFT_EYE_KEY_PREFIX + formKey;
      const rightMethodKey = rightKey + SYMPTOM_METHOD_KEY_SUFFIX;
      const leftMethodKey = leftKey + SYMPTOM_METHOD_KEY_SUFFIX;

      const rawRight = dryEyeForm && dryEyeForm[rightKey];
      const rawLeft = dryEyeForm && dryEyeForm[leftKey];
      const rightMethod = dryEyeForm && dryEyeForm[rightMethodKey];
      const leftMethod = dryEyeForm && dryEyeForm[leftMethodKey];

      const rightValue = this.schemaService.getFriendlyValueName(rawRight, formKey);
      const leftValue = this.schemaService.getFriendlyValueName(rawLeft, formKey);

      const comparedField = {
        value: { right: rightValue, left: leftValue },
        statusRelativeToFirst: {
          right: 'same' as StatusRelativeToFirst,
          left: 'same' as StatusRelativeToFirst
        }
      };

      if (isFirst) {
        firstComparedEncounterValueMap[rightKey] = rawRight;
        firstComparedEncounterValueMap[leftKey] = rawLeft;
        firstComparedEncounterValueMap[rightMethodKey] = rightMethod;
        firstComparedEncounterValueMap[leftMethodKey] = leftMethod;
      } else {
        comparedField['statusRelativeToFirst'] = {
          right: this.getSignTrendStatus(
            formKey,
            firstComparedEncounterValueMap[rightKey],
            rawRight,
            firstComparedEncounterValueMap[rightMethodKey],
            rightMethod
          ),
          left: this.getSignTrendStatus(
            formKey,
            firstComparedEncounterValueMap[leftKey],
            rawLeft,
            firstComparedEncounterValueMap[leftMethodKey],
            leftMethod
          )
        };
      }

      this.normalizedValues['dryEyeForm'][formKey] = {
        [encounter.createdAt]: comparedField,
        ...this.normalizedValues['dryEyeForm'][formKey]
      };
    });
  }

  getCombinedFieldsForAllEncounters(): [Set<any>, Set<any>] {
    let combinedMedicalHistorySet = new Set<any>();
    let combinedSignsSetNotNormalized = new Set<any>();

    this.data.forEach(encounter => {
      if (!encounter.body) return;
      const parsedBody = JSON.parse(encounter.body);
      if (parsedBody.medicalHistoryForm) {
        combinedMedicalHistorySet = new Set([
          ...combinedMedicalHistorySet,
          ...Object.keys(parsedBody.medicalHistoryForm)
        ]);
      }
      if (parsedBody.dryEyeForm) {
        combinedSignsSetNotNormalized = new Set([
          ...combinedSignsSetNotNormalized,
          ...Object.keys(parsedBody.dryEyeForm)
        ]);
      }
    });

    const combinedSignsSet = new Set<any>();
    this.schemaService.symptomKeys.forEach(symptomKey => {
      if (
        combinedSignsSetNotNormalized.has(LEFT_EYE_KEY_PREFIX + symptomKey) ||
        combinedSignsSetNotNormalized.has(RIGHT_EYE_KEY_PREFIX + symptomKey)
      ) {
        combinedSignsSet.add(symptomKey);
      }
    });

    this.fieldsToSkip.forEach(fieldToSkip => {
      combinedMedicalHistorySet.delete(fieldToSkip);
      combinedSignsSet.delete(fieldToSkip);
    });

    return [combinedMedicalHistorySet, combinedSignsSet];
  }

  private canonical(val: any): number | string | null {
    if (val === null || val === undefined) return null;
    const s = typeof val === 'string' ? val.trim() : val;
    if (typeof s === 'string') {
      const upper = s.toUpperCase();
      if (upper === '' || upper === 'N/A' || upper === 'NA' || upper === '-') return null;
    }
    const n = Number(s);
    if (!isNaN(n) && s !== '' && s !== null) return n;
    return String(s).toLowerCase();
  }

  getCombinedSignStatus(left: StatusRelativeToFirst, right: StatusRelativeToFirst): StatusRelativeToFirst {
    const priority: StatusRelativeToFirst[] = ['worsening', 'removed', 'added', 'improving', 'changed', 'same'];
    const li = priority.indexOf(left);
    const ri = priority.indexOf(right);
    if (li === -1 && ri === -1) return 'same';
    if (li === -1) return right;
    if (ri === -1) return left;
    return li <= ri ? left : right;
  }

  getSignEyeStatus(formKey: string, date: string, eye: 'left' | 'right'): StatusRelativeToFirst {
    const fieldData = this.normalizedValues.dryEyeForm[formKey];
    const dateData = fieldData && fieldData[date];
    if (!dateData) return null;
    const status = dateData.statusRelativeToFirst;
    if (!status) return null;
    if (typeof status === 'string') return status as StatusRelativeToFirst;
    return (status as { left: StatusRelativeToFirst; right: StatusRelativeToFirst })[eye];
  }

  getSignTrendStatus(
    formKey: string,
    firstRawValue: any,
    currentRawValue: any,
    firstMethod: any,
    currentMethod: any
  ): StatusRelativeToFirst {
    const a = this.canonical(firstRawValue);
    const b = this.canonical(currentRawValue);

    if (a === null && b !== null) return 'added';
    if (a !== null && b === null) return 'removed';
    if (a === b) return 'same';

    const thermalConfig =
      this.schemaService.getThermalScaleConfig(formKey, currentMethod as SymptomMethod) ||
      this.schemaService.getThermalScaleConfig(formKey, firstMethod as SymptomMethod) ||
      this.schemaService.getThermalScaleConfig(formKey);

    if (thermalConfig) {
      const baselineSeverity = this.thermalScaleService.getColorValue(firstRawValue, thermalConfig);
      const currentSeverity = this.thermalScaleService.getColorValue(currentRawValue, thermalConfig);

      if (baselineSeverity != null && currentSeverity != null) {
        if (currentSeverity < baselineSeverity) return 'improving';
        if (currentSeverity > baselineSeverity) return 'worsening';
        return 'same';
      }
    }

    return 'changed';
  }

  getStatusBasedOnFirstEncounter(firstValue: any, currentValue: any): StatusRelativeToFirst {
    const a = this.canonical(firstValue);
    const b = this.canonical(currentValue);
    if (a === b) return 'same';
    if (a === null && b !== null) return 'added';
    if (a !== null && b === null) return 'removed';
    return 'changed';
  }

  isNumeric(val: any): boolean {
    return !isNaN(parseFloat(val)) && isFinite(val);
  }

  normalizeValue(val: any, maxRange: number = 100): number {
    const num = parseFloat(val && val.toString());
    if (isNaN(num) || isNaN(maxRange) || maxRange === 0) return 0;
    return Math.min((num / maxRange) * 100, 100);
  }

  public getAssessmentMethodFriendlyName(encounter: Assessment): string {
    if (!encounter) return 'N/A';
    if (this.assessmentMethodCache[encounter.id] !== undefined) {
      return this.assessmentMethodCache[encounter.id];
    }
    try {
      const parsed = encounter.body ? JSON.parse(encounter.body) : null;
      const method =
        parsed && parsed.dryEyeForm && parsed.dryEyeForm.assessmentMethod
          ? parsed.dryEyeForm.assessmentMethod
          : null;
      const name =
        (method && this.assessmentService.encounterMethodFriendlyName[method]) || method || 'N/A';
      this.assessmentMethodCache[encounter.id] = name;
      return name;
    } catch {
      return 'N/A';
    }
  }

  public setMove(index: number, direction: 'left' | 'right') {
    const neighborIndex = index + (direction === 'right' ? 1 : -1);
    const d1 = this.data[index] && this.data[index].createdAt;
    const d2 = this.data[neighborIndex] && this.data[neighborIndex].createdAt;

    const newData = [...this.data];
    const [movedEncounter] = newData.splice(index, 1);
    newData.splice(neighborIndex, 0, movedEncounter);
    this.data = newData;
    this.assessmentMethodCache = {};
    this.normalize();

    if (d1) this.highlightDates[d1] = direction;
    if (d2) this.highlightDates[d2] = direction === 'right' ? 'left' : 'right';
    this.moveHighlightTimeout = setTimeout(() => {
      if (d1) delete this.highlightDates[d1];
      if (d2) delete this.highlightDates[d2];
    }, 800);
  }

  public setRemove(index: number) {
    this.data = this.data.filter((_, i) => i !== index);
    this.assessmentMethodCache = {};
    this.normalize();
  }

  openAddAssessmentsDialog() {
    const remaining = Math.max(this.MAX_COMPARE - (this.data ? this.data.length : 0), 0);

    const dialogRef = this.dialog.open(SelectAssessmentsModalComponent, {
      width: '600px',
      data: {
        currentCompared: (this.data || []).slice(),
        unaddedAssessments: (this.encountersNotSelectedForComparison || []).slice(),
        maxSelectable: remaining
      }
    });

    dialogRef.afterClosed().subscribe((selection: Assessment[]) => {
      if (!selection) return;

      if (remaining === 0) {
        this.data = selection.slice(0, this.MAX_COMPARE);
      } else {
        const existingIds = new Set((this.data || []).map(a => a.id));
        const additions = selection.filter(a => !existingIds.has(a.id)).slice(0, remaining);
        if (additions.length === 0) return;
        this.data = (this.data || []).concat(additions);
      }

      this.assessmentMethodCache = {};
      this.normalize();
    });
  }

  get gridTemplateColumns(): string {
    const dateCount =
      (this.encounterDateOrder && this.encounterDateOrder.length) ||
      (this.data && this.data.length) ||
      0;
    return ['minmax(200px,1fr)', ...Array(Math.max(dateCount, 1)).fill('1fr')].join(' ');
  }

  get baselineDate(): string {
    return this.encounterDateOrder && this.encounterDateOrder.length
      ? this.encounterDateOrder[0]
      : null;
  }

  get stickyHeaderGrid(): string {
    const dateCount = this.encounterDateOrder ? this.encounterDateOrder.length : 0;
    return ['minmax(200px,1fr)', ...Array(dateCount).fill('1fr')].join(' ');
  }
}
